import { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Form, Input, Button, Checkbox, Alert } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { EnvBanner } from '@/components/EnvBanner';
import { sessionHelper } from '@/auth/sessionHelper';

interface LoginFormValues {
  username: string;
  password: string;
  rememberMe: boolean;
}

// Split-screen login. Left: dark hero canvas with the BUILD · GOVERN · SHIP
// theme rendered as massive stacked verbs with gradient text-fill. Right:
// clean form panel. Aurora-style background animation gives premium feel
// without crossing into gimmick territory.

export const LoginPage = observer(function LoginPage() {
  const { authStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm<LoginFormValues>();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Where to send the user after a successful login ───────────────────
  // Three possible sources, in priority order:
  //   1. React Router state — used by <ProtectedRoute> when it bounces
  //      an unauthenticated user mid-navigation (passes { from: pathname })
  //   2. sessionHelper.getReturnTo() — stamped by the API client when a
  //      live session expired and the user got force-redirected
  //   3. "/" — first-time visit fallback
  const from = useMemo<string>(
    () =>
      (location.state as { from?: string } | undefined)?.from
      ?? sessionHelper.getReturnTo()
      ?? '/',
    [location.state],
  );

  // ── Friendly notice when this isn't a cold first-visit ────────────────
  // The API client appends ?session=expired to the /login URL when it
  // forces the redirect on a 401 + failed refresh. Detect that here and
  // surface a soft notice instead of leaving the form looking like a
  // first-time sign-in.
  const sessionExpired = useMemo<boolean>(() => {
    return new URLSearchParams(location.search).get('session') === 'expired';
  }, [location.search]);

  async function onFinish(values: LoginFormValues): Promise<void> {
    setSubmitError(null);
    try {
      await authStore.login(values.username, values.password, values.rememberMe);
      // Clear the stamped returnTo so a future login (e.g. account
      // switch) doesn't reuse this path.
      sessionHelper.clearReturnTo();
      navigate(from, { replace: true });
    } catch {
      setSubmitError(authStore.loginError ?? 'Sign in failed');
    }
  }

  return (
    <>
      {/* Aurora keyframes injected once for the page */}
      <style>{`
        @keyframes aurora-1 {
          0%, 100% { transform: translate(-10%, -10%) rotate(0deg); }
          50% { transform: translate(15%, 10%) rotate(15deg); }
        }
        @keyframes aurora-2 {
          0%, 100% { transform: translate(20%, 30%) rotate(0deg); }
          50% { transform: translate(-10%, -20%) rotate(-20deg); }
        }
        @keyframes aurora-3 {
          0%, 100% { transform: translate(-30%, 40%) rotate(0deg); }
          50% { transform: translate(40%, -30%) rotate(25deg); }
        }
        @keyframes verb-rise {
          from { opacity: 0; transform: translateY(28px); letter-spacing: -0.02em; }
          to { opacity: 1; transform: translateY(0); letter-spacing: -0.045em; }
        }
        @keyframes wordmark-fade {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes form-fade {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        .as-verb {
          font-family: 'Inter', system-ui, sans-serif;
          font-weight: 900;
          font-size: clamp(72px, 9vw, 140px);
          line-height: 0.92;
          letter-spacing: -0.045em;
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          opacity: 0;
          animation: verb-rise 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards,
                     shimmer 8s linear infinite;
          animation-delay: var(--delay), 0.8s;
          margin: 0;
        }
        .as-verb-build {
          background-image: linear-gradient(110deg, #DBEAFE 0%, #3B82F6 25%, #2563EB 50%, #3B82F6 75%, #DBEAFE 100%);
        }
        .as-verb-govern {
          background-image: linear-gradient(110deg, #EDE9FE 0%, #8B5CF6 25%, #7C3AED 50%, #8B5CF6 75%, #EDE9FE 100%);
        }
        .as-verb-ship {
          background-image: linear-gradient(110deg, #D1FAE5 0%, #10B981 25%, #059669 50%, #10B981 75%, #D1FAE5 100%);
        }
        .as-wordmark {
          opacity: 0;
          animation: wordmark-fade 500ms cubic-bezier(0.22, 1, 0.36, 1) 0.2s forwards;
        }
        .as-tagline {
          opacity: 0;
          animation: wordmark-fade 500ms cubic-bezier(0.22, 1, 0.36, 1) 1.4s forwards;
        }
        .as-form-pane {
          opacity: 0;
          animation: form-fade 500ms cubic-bezier(0.22, 1, 0.36, 1) 0.4s forwards;
        }
        .as-stats {
          opacity: 0;
          animation: wordmark-fade 500ms cubic-bezier(0.22, 1, 0.36, 1) 1.7s forwards;
        }
      `}</style>

      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <EnvBanner />
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) minmax(380px, 0.9fr)',
            minHeight: 0,
          }}
        >
          {/* ─────────────── LEFT — Hero canvas ─────────────── */}
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              background: `linear-gradient(135deg, #020617 0%, ${colors.slate[900]} 35%, #1E1B4B 70%, #2E1065 100%)`,
              padding: '64px 72px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              color: '#FFFFFF',
            }}
          >
            {/* Aurora gradient orbs (animated) */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '-20%',
                  left: '-10%',
                  width: '70vw',
                  height: '70vw',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${colors.ink[600]}40 0%, transparent 60%)`,
                  filter: 'blur(60px)',
                  animation: 'aurora-1 18s ease-in-out infinite',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-30%',
                  right: '-20%',
                  width: '80vw',
                  height: '80vw',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${colors.violet[600]}50 0%, transparent 65%)`,
                  filter: 'blur(80px)',
                  animation: 'aurora-2 22s ease-in-out infinite',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '30%',
                  left: '40%',
                  width: '50vw',
                  height: '50vw',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${colors.green[600]}30 0%, transparent 70%)`,
                  filter: 'blur(70px)',
                  animation: 'aurora-3 26s ease-in-out infinite',
                }}
              />
              {/* Subtle grain overlay for depth */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.04 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                  opacity: 0.6,
                  mixBlendMode: 'overlay',
                }}
              />
            </div>

            {/* Wordmark — top */}
            <div
              className="as-wordmark"
              style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 44,
                  borderRadius: 3,
                  background: `linear-gradient(180deg, ${colors.ink[500]}, ${colors.violet[500]})`,
                  boxShadow: `0 0 24px ${colors.ink[500]}aa`,
                }}
              />
              <span
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: colors.slate[100],
                }}
              >
                Agent Studio
              </span>
            </div>

            {/* Hero stack — Build. Govern. Ship. */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h1
                className="as-verb as-verb-build"
                style={{ ['--delay' as 'color']: '0.6s' } as React.CSSProperties}
              >
                Build.
              </h1>
              <h1
                className="as-verb as-verb-govern"
                style={{ ['--delay' as 'color']: '0.85s' } as React.CSSProperties}
              >
                Govern.
              </h1>
              <h1
                className="as-verb as-verb-ship"
                style={{ ['--delay' as 'color']: '1.1s' } as React.CSSProperties}
              >
                Ship.
              </h1>

              <div
                className="as-tagline"
                style={{
                  marginTop: 28,
                  fontSize: 18,
                  lineHeight: 1.5,
                  color: colors.slate[300],
                  maxWidth: 520,
                  fontWeight: 400,
                  letterSpacing: '-0.005em',
                }}
              >
                The platform for production-grade AI agents — from first scenario to
                live traffic, with everything in between.
              </div>
            </div>

            {/* Stats / footer marks */}
            <div
              className="as-stats"
              style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                gap: 36,
                flexWrap: 'wrap',
              }}
            >
              <StatBlock label="Knowledge" value="RAG-grounded" />
              <StatBlock label="Validation" value="Multi-turn" />
              <StatBlock label="Observability" value="OTel-native" />
            </div>
          </div>

          {/* ─────────────── RIGHT — Sign-in panel ─────────────── */}
          <div
            className="as-form-pane"
            style={{
              background: '#FFFFFF',
              padding: '64px 56px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 0,
            }}
          >
            <div style={{ maxWidth: 380, width: '100%', alignSelf: 'center' }}>
              <div style={{ marginBottom: 36 }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: 32,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: colors.slate[900],
                    lineHeight: 1.15,
                  }}
                >
                  Sign in
                </h2>
              </div>

              {/* Soft notice when the user landed here because their
                  session expired mid-flow. Distinct from the loud
                  red error below — this is informational, not a
                  failure. Hidden once the user submits and an actual
                  error replaces it. */}
              {sessionExpired && !submitError && (
                <Alert
                  type="info"
                  message="Your session expired"
                  description="Sign back in and we'll drop you right back where you were."
                  showIcon
                  style={{ marginBottom: 20, borderRadius: 10 }}
                />
              )}

              {submitError && (
                <Alert
                  type="error"
                  message={submitError}
                  showIcon
                  style={{ marginBottom: 20, borderRadius: 10 }}
                  closable
                  onClose={() => setSubmitError(null)}
                />
              )}

              <Form
                form={form}
                layout="vertical"
                initialValues={{ rememberMe: false }}
                onFinish={onFinish}
                autoComplete="on"
                size="large"
                requiredMark={false}
              >
                <Form.Item
                  name="username"
                  label={<FormLabel>Username</FormLabel>}
                  rules={[{ required: true, message: 'Username is required' }]}
                  style={{ marginBottom: 18 }}
                >
                  <Input
                    autoFocus
                    autoComplete="username"
                    placeholder="you@yourorg"
                    style={{
                      height: 48,
                      borderRadius: 10,
                      fontSize: 14,
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={<FormLabel>Password</FormLabel>}
                  rules={[{ required: true, message: 'Password is required' }]}
                  style={{ marginBottom: 18 }}
                >
                  <Input.Password
                    autoComplete="current-password"
                    placeholder="••••••••"
                    style={{
                      height: 48,
                      borderRadius: 10,
                      fontSize: 14,
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="rememberMe"
                  valuePropName="checked"
                  style={{ marginBottom: 24 }}
                >
                  <Checkbox style={{ fontSize: 13, color: colors.slate[600] }}>
                    Keep me signed in on this device
                  </Checkbox>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={authStore.isLoggingIn}
                    block
                    style={{
                      height: 50,
                      fontSize: 15,
                      fontWeight: 600,
                      borderRadius: 10,
                      background: `linear-gradient(135deg, ${colors.ink[600]} 0%, ${colors.violet[600]} 100%)`,
                      border: 'none',
                      boxShadow: `0 4px 16px ${colors.ink[600]}40`,
                      letterSpacing: '0.005em',
                    }}
                  >
                    Sign in →
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

// ── Stat block on hero ──────────────────────────────────────────────────────

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: colors.slate[400],
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: colors.slate[100],
          letterSpacing: '-0.005em',
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ── Form label ──────────────────────────────────────────────────────────────

function FormLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: colors.slate[700],
        letterSpacing: '0.005em',
      }}
    >
      {children}
    </span>
  );
}
